/**
 * Companion Planting — Curated Guild Templates
 *
 * 15 hand-authored UK kitchen-garden guilds. Each template defines:
 *  - A hero plant (the anchor crop)
 *  - Required + optional companions with explicit roles
 *  - Density rules (per m²) and grid placement zones
 *  - Bespoke "why this works" rationale
 *
 * Roles drive WHERE a plant goes in the bed:
 *   anchor    → centre / dominant block
 *   interplant → between anchors (basil between toms)
 *   border    → front edge row
 *   corner    → 4 corners only (perennial herbs, trap crops)
 *   climber   → uses anchor as trellis (beans up corn)
 *   sprawler  → fills floor between/around anchors (squash)
 *   trap      → outer ring, sacrificial pest decoy
 */

export type PlantRole =
  | 'anchor'
  | 'interplant'
  | 'border'
  | 'corner'
  | 'climber'
  | 'sprawler'
  | 'trap';

export interface TemplateSlot {
  /** Canonical plant name — matched case-insensitively against user's garden */
  name: string;
  role: PlantRole;
  /** plants per m² at "balanced" density */
  perSqM: number;
  /** if false, template can still build without this plant */
  required: boolean;
  /** rationale fragment used in "Why this works" */
  why: string;
}

export interface GuildTemplate {
  id: string;
  name: string;
  /** Names of plants any of which can serve as the hero (case-insensitive) */
  heroes: string[];
  slots: TemplateSlot[];
  /** Top-level summary for the rationale block */
  summary: string;
  /** Minimum bed area (m²) below which this template is impractical */
  minAreaM2?: number;
}

/**
 * Density multipliers applied to perSqM. Sparse = roomier (fewer plants),
 * Packed = intensive square-foot style.
 */
export const DENSITY_MULTIPLIER: Record<'sparse' | 'balanced' | 'packed', number> = {
  sparse: 0.7,
  balanced: 1.0,
  packed: 1.4,
};

export const TEMPLATES: GuildTemplate[] = [
  {
    id: 'three-sisters',
    name: 'Three Sisters',
    heroes: ['Sweetcorn', 'Corn'],
    minAreaM2: 1.5,
    summary:
      'The classic Native American polyculture: corn provides a living trellis, beans fix nitrogen for the corn, and squash sprawls beneath to smother weeds and lock in moisture.',
    slots: [
      { name: 'Sweetcorn', role: 'anchor', perSqM: 4, required: true, why: 'Tall stalks act as a living trellis for the beans.' },
      { name: 'Runner Beans', role: 'climber', perSqM: 4, required: false, why: 'Climb the corn and fix nitrogen into the soil.' },
      { name: 'French Beans', role: 'climber', perSqM: 4, required: false, why: 'Climb the corn and fix nitrogen into the soil.' },
      { name: 'Squash', role: 'sprawler', perSqM: 1, required: false, why: 'Big leaves shade out weeds and keep moisture in.' },
      { name: 'Courgette', role: 'sprawler', perSqM: 1, required: false, why: 'Big leaves shade out weeds and keep moisture in.' },
      { name: 'Pumpkin', role: 'sprawler', perSqM: 1, required: false, why: 'Big leaves shade out weeds and keep moisture in.' },
      { name: 'Marigolds', role: 'border', perSqM: 4, required: false, why: 'Front edge — repels nematodes and adds colour.' },
    ],
  },
  {
    id: 'tomato-trio',
    name: 'Tomato Trio',
    heroes: ['Tomatoes', 'Tomato'],
    summary:
      'Tomatoes thrive with aromatic basil interplanted at their feet, marigolds along the front to repel whitefly and root pests, and nasturtiums tucked in the corners as an aphid trap crop.',
    slots: [
      { name: 'Tomatoes', role: 'anchor', perSqM: 3, required: true, why: 'Main crop — needs sun, support and consistent water.' },
      { name: 'Basil', role: 'interplant', perSqM: 6, required: false, why: 'Repels whitefly and is said to improve tomato flavour.' },
      { name: 'Marigolds', role: 'border', perSqM: 4, required: false, why: 'Front-edge defenders — deter whitefly and root nematodes.' },
      { name: 'Nasturtiums', role: 'corner', perSqM: 2, required: false, why: 'Corner trap crop — aphids prefer them to your tomatoes.' },
      { name: 'Parsley', role: 'interplant', perSqM: 6, required: false, why: 'Attracts hoverflies that prey on aphids.' },
    ],
  },
  {
    id: 'salad-bed',
    name: 'Salad Bed',
    heroes: ['Lettuce'],
    summary:
      'Cool-season salad polyculture: lettuce shades the soil for shallow-rooted carrots and onions; radishes break crusty soil and crop fast; spinach fills any gaps.',
    slots: [
      { name: 'Lettuce', role: 'anchor', perSqM: 9, required: true, why: 'Shades the soil and keeps roots of neighbours cool.' },
      { name: 'Carrots', role: 'interplant', perSqM: 36, required: false, why: 'Onions confuse the carrot fly — classic pairing.' },
      { name: 'Onions', role: 'interplant', perSqM: 16, required: false, why: 'Mask the carrot scent and deter aphids.' },
      { name: 'Radishes', role: 'interplant', perSqM: 36, required: false, why: 'Fast catch crop — harvested before lettuce matures.' },
      { name: 'Spinach', role: 'border', perSqM: 9, required: false, why: 'Front edge — uses gaps and tolerates light shade.' },
    ],
  },
  {
    id: 'brassica-patrol',
    name: 'Brassica Patrol',
    heroes: ['Cabbage', 'Kale', 'Broccoli', 'Cauliflower', 'Brussels Sprouts'],
    summary:
      'Brassicas attract a long list of pests; this guild surrounds them with allies. Dill brings parasitic wasps, nasturtiums divert cabbage white butterflies, onions confuse aphids.',
    slots: [
      { name: 'Cabbage', role: 'anchor', perSqM: 2, required: false, why: 'Heart of the brassica family — heavy feeder.' },
      { name: 'Kale', role: 'anchor', perSqM: 2, required: false, why: 'Productive long-season brassica.' },
      { name: 'Broccoli', role: 'anchor', perSqM: 2, required: false, why: 'Heavy feeder, benefits from interplanting.' },
      { name: 'Dill', role: 'interplant', perSqM: 4, required: false, why: 'Hosts parasitic wasps that hunt cabbage caterpillars.' },
      { name: 'Onions', role: 'border', perSqM: 16, required: false, why: 'Aphid deterrent along the front.' },
      { name: 'Nasturtiums', role: 'trap', perSqM: 2, required: false, why: 'Trap crop — cabbage whites lay eggs here instead.' },
    ],
  },
  {
    id: 'cucumber-tower',
    name: 'Cucumber Tower',
    heroes: ['Cucumber', 'Cucumbers'],
    minAreaM2: 1.0,
    summary:
      'Cucumbers grow vertically up sunflower stems, saving floor space. Dill attracts pollinators and beneficial wasps; beans add nitrogen for the hungry vines.',
    slots: [
      { name: 'Sunflowers', role: 'anchor', perSqM: 2, required: false, why: 'Living trellis — cucumber tendrils climb the stems.' },
      { name: 'Cucumber', role: 'climber', perSqM: 3, required: true, why: 'Trains up the sunflower stalks for vertical yield.' },
      { name: 'Dill', role: 'interplant', perSqM: 4, required: false, why: 'Attracts pollinators and predatory insects.' },
      { name: 'French Beans', role: 'border', perSqM: 9, required: false, why: 'Front row — fix nitrogen for hungry cucumbers.' },
    ],
  },
  {
    id: 'strawberry-carpet',
    name: 'Strawberry Carpet',
    heroes: ['Strawberries', 'Strawberry'],
    summary:
      'Strawberries leave gaps in spring and after fruiting — fill them with quick salad crops. Chives along the edge deter aphids and slugs.',
    slots: [
      { name: 'Strawberries', role: 'anchor', perSqM: 4, required: true, why: 'Anchor — leaves the bed for years and fruits early summer.' },
      { name: 'Spinach', role: 'interplant', perSqM: 9, required: false, why: 'Fills spring gaps before strawberry leaves take over.' },
      { name: 'Lettuce', role: 'interplant', perSqM: 9, required: false, why: 'Quick succession crop in the spaces between plants.' },
      { name: 'Chives', role: 'border', perSqM: 9, required: false, why: 'Edge planting — deters aphids and looks pretty in flower.' },
    ],
  },
  {
    id: 'salsa-garden',
    name: 'Salsa Garden',
    heroes: ['Peppers', 'Pepper', 'Chillies', 'Chili'],
    summary:
      'Mediterranean trio: peppers like warm roots, basil shares the same conditions and deters thrips, low spinach acts as living mulch.',
    slots: [
      { name: 'Peppers', role: 'anchor', perSqM: 4, required: true, why: 'Heat-loving anchor — needs sun and shelter.' },
      { name: 'Basil', role: 'interplant', perSqM: 6, required: false, why: 'Same conditions as peppers — deters thrips and aphids.' },
      { name: 'Spinach', role: 'border', perSqM: 9, required: false, why: 'Low ground cover — uses space peppers ignore.' },
      { name: 'Nasturtiums', role: 'trap', perSqM: 2, required: false, why: 'Corner trap crop for aphids.' },
    ],
  },
  {
    id: 'mediterranean-herbs',
    name: 'Mediterranean Herbs',
    heroes: ['Rosemary', 'Thyme', 'Sage', 'Oregano', 'Lavender'],
    summary:
      'Drought-tolerant perennials that share lean, free-draining soil. Plant once, harvest for years — and the flowers bring in pollinators.',
    slots: [
      { name: 'Rosemary', role: 'anchor', perSqM: 1, required: false, why: 'Evergreen anchor — gives structure year-round.' },
      { name: 'Sage', role: 'anchor', perSqM: 2, required: false, why: 'Bushy perennial — pairs with rosemary on dry soil.' },
      { name: 'Thyme', role: 'border', perSqM: 9, required: false, why: 'Low-growing edge — flowers loved by bees.' },
      { name: 'Oregano', role: 'interplant', perSqM: 4, required: false, why: 'Spreads happily between woody anchors.' },
      { name: 'Lavender', role: 'corner', perSqM: 1, required: false, why: 'Corner accents — pollinator magnet.' },
    ],
  },
  {
    id: 'root-mix',
    name: 'Root Mix',
    heroes: ['Carrots', 'Carrot'],
    summary:
      'Roots at different depths share a bed without competing. Onions throw the carrot fly off the scent; radishes mark the rows and break soil for beetroot.',
    slots: [
      { name: 'Carrots', role: 'anchor', perSqM: 36, required: true, why: 'Deep tap roots — main crop.' },
      { name: 'Onions', role: 'interplant', perSqM: 16, required: false, why: 'Mask carrot scent — deters carrot fly.' },
      { name: 'Beetroot', role: 'interplant', perSqM: 9, required: false, why: 'Different root depth — no competition.' },
      { name: 'Radishes', role: 'interplant', perSqM: 36, required: false, why: 'Quick crop — marks rows and loosens soil.' },
      { name: 'Lettuce', role: 'border', perSqM: 9, required: false, why: 'Front edge — shallow roots, fast harvest.' },
    ],
  },
  {
    id: 'pollinator-strip',
    name: 'Pollinator Strip',
    heroes: ['Sunflowers', 'Sunflower', 'Calendula', 'Borage'],
    summary:
      'A working flower bed that pulls bees, hoverflies and lacewings into your garden. Place near vegetable beds and watch yields jump.',
    slots: [
      { name: 'Sunflowers', role: 'anchor', perSqM: 2, required: false, why: 'Tall back — late nectar source for bees.' },
      { name: 'Borage', role: 'interplant', perSqM: 2, required: false, why: 'Bee magnet — flowers all summer.' },
      { name: 'Calendula', role: 'interplant', perSqM: 4, required: false, why: 'Hoverflies feed here, then prey on aphids next door.' },
      { name: 'Nasturtiums', role: 'border', perSqM: 2, required: false, why: 'Front edge — trap crop and edible flowers.' },
      { name: 'Dill', role: 'interplant', perSqM: 4, required: false, why: 'Attracts parasitic wasps.' },
    ],
  },
  {
    id: 'allium-border',
    name: 'Allium Border',
    heroes: ['Garlic', 'Onions', 'Onion'],
    summary:
      'Alliums make a pest-deterrent border for milder crops. Carrots and beetroot interplant happily; lettuce uses the edges.',
    slots: [
      { name: 'Garlic', role: 'anchor', perSqM: 16, required: false, why: 'Aphid and fungal deterrent — overwinters in the ground.' },
      { name: 'Onions', role: 'anchor', perSqM: 16, required: false, why: 'Productive bulbs — protects the rest of the bed.' },
      { name: 'Carrots', role: 'interplant', perSqM: 36, required: false, why: 'Classic carrot/onion pairing — mutual pest control.' },
      { name: 'Beetroot', role: 'interplant', perSqM: 9, required: false, why: 'Different root depth — fits between alliums.' },
      { name: 'Lettuce', role: 'border', perSqM: 9, required: false, why: 'Quick edge crop.' },
    ],
  },
  {
    id: 'squash-sprawl',
    name: 'Squash Sprawl',
    heroes: ['Squash', 'Pumpkin', 'Courgette', 'Courgettes'],
    minAreaM2: 1.5,
    summary:
      'Big sprawlers need room — pair them with quick crops that finish before they spread, plus flowers that bring pollinators for the female blooms.',
    slots: [
      { name: 'Courgette', role: 'anchor', perSqM: 1, required: false, why: 'Heavy feeder — needs space and sun.' },
      { name: 'Squash', role: 'anchor', perSqM: 1, required: false, why: 'Sprawls — give it room or train upward.' },
      { name: 'Pumpkin', role: 'anchor', perSqM: 0.5, required: false, why: 'Biggest sprawler — needs the most space.' },
      { name: 'Nasturtiums', role: 'border', perSqM: 2, required: false, why: 'Trap crop for squash bugs and edible flowers.' },
      { name: 'Marigolds', role: 'corner', perSqM: 4, required: false, why: 'Corner colour — deters root nematodes.' },
      { name: 'Sweetcorn', role: 'interplant', perSqM: 4, required: false, why: 'Tall corn lets squash sprawl below — Three Sisters style.' },
    ],
  },
  {
    id: 'bean-tepee',
    name: 'Bean Tepee',
    heroes: ['Runner Beans', 'French Beans', 'Beans'],
    summary:
      'Beans climb a wigwam in the centre; sweetcorn and squash echo the Three Sisters; marigolds keep blackfly off the bean tips.',
    slots: [
      { name: 'Runner Beans', role: 'anchor', perSqM: 6, required: false, why: 'Climbs a centre wigwam — fixes nitrogen.' },
      { name: 'French Beans', role: 'anchor', perSqM: 9, required: false, why: 'Climbing variety — productive over a long season.' },
      { name: 'Sweetcorn', role: 'interplant', perSqM: 4, required: false, why: 'Alternative trellis if no wigwam — Three Sisters echo.' },
      { name: 'Squash', role: 'sprawler', perSqM: 1, required: false, why: 'Floor-level — shades soil and smothers weeds.' },
      { name: 'Marigolds', role: 'border', perSqM: 4, required: false, why: 'Deters blackfly that target young bean tips.' },
    ],
  },
  {
    id: 'berry-patch',
    name: 'Berry Patch',
    heroes: ['Raspberries', 'Raspberry', 'Blackcurrant', 'Blackcurrants', 'Redcurrant', 'Redcurrants', 'Gooseberry', 'Gooseberries'],
    minAreaM2: 1.5,
    summary:
      'Soft-fruit bushes with allium underplanting to deter aphids and a chive border that flowers when bees are needed for pollination.',
    slots: [
      { name: 'Raspberries', role: 'anchor', perSqM: 1, required: false, why: 'Back row — canes need support and full sun.' },
      { name: 'Blackcurrant', role: 'anchor', perSqM: 0.5, required: false, why: 'Bush anchor — heavy cropper.' },
      { name: 'Redcurrant', role: 'anchor', perSqM: 0.5, required: false, why: 'Bush anchor — pairs with blackcurrant.' },
      { name: 'Gooseberry', role: 'anchor', perSqM: 0.5, required: false, why: 'Spiny bush — keeps cats out!' },
      { name: 'Garlic', role: 'interplant', perSqM: 16, required: false, why: 'Aphid and fungal deterrent under bushes.' },
      { name: 'Chives', role: 'border', perSqM: 9, required: false, why: 'Front edge — flowers feed pollinators.' },
    ],
  },
  {
    id: 'cottage-mix',
    name: 'Cottage Mix',
    heroes: ['Calendula', 'Borage', 'Nasturtiums', 'Nasturtium'],
    summary:
      'Edible flowers, soft herbs and quick salad crops in a relaxed cottage-garden style. Beautiful, productive, and a pollinator buffet.',
    slots: [
      { name: 'Calendula', role: 'anchor', perSqM: 4, required: false, why: 'Edible petals — flowers all season.' },
      { name: 'Borage', role: 'anchor', perSqM: 2, required: false, why: 'Bee magnet — self-seeds for next year.' },
      { name: 'Nasturtiums', role: 'border', perSqM: 2, required: false, why: 'Trailing edge — peppery leaves and flowers.' },
      { name: 'Chives', role: 'corner', perSqM: 9, required: false, why: 'Corner clumps — purple flowers feed bees.' },
      { name: 'Lettuce', role: 'interplant', perSqM: 9, required: false, why: 'Tucked among flowers — easy salad pickings.' },
    ],
  },
];

/** Find templates whose hero list includes the given plant name (case-insensitive). */
export function findTemplatesForHero(plantName: string): GuildTemplate[] {
  const n = plantName.trim().toLowerCase();
  return TEMPLATES.filter((t) => t.heroes.some((h) => h.toLowerCase() === n));
}

/** Get a template by id. */
export function getTemplate(id: string): GuildTemplate | undefined {
  return TEMPLATES.find((t) => t.id === id);
}

/** Plants this template *can* use (for matching against user's garden / suggesting additions). */
export function templatePlantNames(t: GuildTemplate): string[] {
  return t.slots.map((s) => s.name);
}
